import type { ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/utils/classnames';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { MaterialIcon } from './MaterialIcon';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Description rendered under the title (e.g. sensitivity notice). */
  description?: ReactNode;
  /** Width on desktop. Mobile (<640px) is forced to full screen. Defaults to 480px. */
  width?: number;
  /** Anchor side. Defaults to "right" — the canonical audit detail drawer. */
  side?: 'left' | 'right';
  /** Hide the default header (close button still rendered). */
  hideHeader?: boolean;
  /** Skip the inner padding (useful when children render their own chrome). */
  noPadding?: boolean;
  children: ReactNode;
}

export function Drawer({
  open,
  onClose,
  title,
  description,
  width = 480,
  side = 'right',
  hideHeader = false,
  noPadding = false,
  children,
}: DrawerProps) {
  const isMobile = useIsMobile();
  const effectiveWidth = isMobile ? '100vw' : `${width}px`;
  const initialX = side === 'right' ? '100%' : '-100%';
  const sideClass = side === 'right' ? 'right-0 border-l' : 'left-0 border-r';
  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="drawer-backdrop"
            className="fixed inset-0 z-50 bg-inverse-surface/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            onClick={onClose}
          />
          <motion.aside
            key="drawer-panel"
            role="dialog"
            aria-modal="true"
            className={cn(
              'fixed top-0 z-50 flex h-screen flex-col',
              'bg-surface border-outline-variant shadow-2xl',
              sideClass,
            )}
            style={{ width: effectiveWidth, maxWidth: '100vw' }}
            initial={{ x: initialX }}
            animate={{ x: 0 }}
            exit={{ x: initialX }}
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          >
            {!hideHeader ? (
              <header className="flex items-start justify-between gap-stack-sm border-b border-outline-variant px-container-padding py-stack-md">
                <div className="min-w-0 flex-1">
                  {title ? (
                    <h2 className="font-headline-md text-headline-md text-on-surface">{title}</h2>
                  ) : null}
                  {description ? (
                    <p className="mt-unit text-body-sm text-on-surface-variant">{description}</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex h-8 w-8 items-center justify-center rounded text-on-surface-variant hover:bg-surface-container-high"
                  aria-label="关闭"
                >
                  <MaterialIcon name="close" size={20} />
                </button>
              </header>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="absolute right-stack-md top-stack-md z-10 flex h-8 w-8 items-center justify-center rounded text-on-surface-variant hover:bg-surface-container-high"
                aria-label="关闭"
              >
                <MaterialIcon name="close" size={20} />
              </button>
            )}
            <div
              className={cn(
                'flex-1 overflow-y-auto',
                noPadding ? '' : 'px-container-padding py-stack-md',
              )}
            >
              {children}
            </div>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}
